import React, { useEffect, useRef } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import { getAccessToken } from "./getAccessToken";
import { loginRequest } from "./authConfig";

const API_BASE = "/api";

export default function RequestInterceptor({ children }) {
  const { instance, accounts } = useMsal();

  const instanceRef = useRef(instance);
  const accountRef = useRef(null);
  const scopesRef = useRef(loginRequest.scopes);

  useEffect(() => {
    instanceRef.current = instance;

    const active = instance.getActiveAccount?.() || null;
    accountRef.current = active || accounts?.[0] || null;

    scopesRef.current = loginRequest.scopes;
  }, [instance, accounts]);

  useEffect(() => {
    const shouldAttachToken = (url) => {
      if (!url) return false;
      if (!API_BASE) return true;

      try {
        const a = document.createElement("a");
        a.href = url;
        const path = a.pathname || url;
        return path.startsWith(API_BASE) || url.startsWith(API_BASE);
      } catch {
        return url.startsWith(API_BASE);
      }
    };

    const reqId = axios.interceptors.request.use(async (config) => {
      if (!config?.url || !shouldAttachToken(config.url)) return config;

      const acc = accountRef.current;
      const inst = instanceRef.current;
      const scopes = scopesRef.current;

      if (!acc) return config;

      try {
        const accessToken = await getAccessToken(inst, acc, scopes);
        if (accessToken) {
          config.headers = {
            ...(config.headers || {}),
            Authorization: `Bearer ${accessToken}`,
          };
        }
      } catch {
        // swallow
      }

      return config;
    });

    const resId = axios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const config = error?.config;
        const status = error?.response?.status;

        if (!config) return Promise.reject(error);

        config._retry = config._retry ?? 0;

        const acc = accountRef.current;
        const inst = instanceRef.current;
        const scopes = scopesRef.current;

        if (status === 401 && acc && config._retry < 1) {
          config._retry += 1;

          try {
            const accessToken = await getAccessToken(inst, acc, scopes, true);
            if (accessToken) {
              config.headers = {
                ...(config.headers || {}),
                Authorization: `Bearer ${accessToken}`,
              };
              return axios(config);
            }

            window.dispatchEvent(new CustomEvent("auth:interaction-required"));
          } catch {
            // swallow
          }
        }

        if (status === 403) {
          window.dispatchEvent(new CustomEvent("auth:roles-maybe-changed"));
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, []); 

  return <>{children}</>;
}